// components/layout/AppHeader.tsx
// Unified header used across landing page, dashboard, and profile setup.
// Logo on the left, UserHeader on the right, optional content in between.
import React from 'react';
import { Link } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import UserHeader from '@/components/layout/UserHeader';
import { cn } from '@/lib/utils';

const NEXILINK_LOGO_URL =
    'https://cdn.builder.io/api/v1/image/assets/89bc6265b1a84995b7b85b1453a49e93/e937596a79e27aedd1ca37344ef2209cd6e8c5da2cc900e6c0f5467b6176325e?apiKey=89bc6265b1a84995b7b85b1453a49e93&';

interface AppHeaderProps {
    /** Show hamburger menu button (dashboard mobile) */
    onMenuClick?: () => void;
    /** Content between logo and user menu (nav links, page title, etc.) */
    children?: React.ReactNode;
    /** Extra content on the right side, before UserHeader (e.g. login buttons) */
    rightContent?: React.ReactNode;
    /** Add shadow based on scroll state */
    scrolled?: boolean;
    /** Fixed positioning (landing page) vs sticky (dashboard) */
    position?: 'fixed' | 'sticky';
    /** Hide the logo (e.g. when the sidebar already shows it) */
    hideLogo?: boolean;
    /** Additional className */
    className?: string;
}

const AppHeader: React.FC<AppHeaderProps> = ({
    onMenuClick,
    children,
    rightContent,
    scrolled = false,
    position = 'sticky',
    hideLogo = false,
    className,
}) => {
    const { user } = useAuth();

    return (
        <header
            className={cn(
                'top-0 z-40 transition-all duration-300',
                position === 'fixed' ? 'fixed left-0 right-0' : 'sticky',
                'border-b',
                // Fixed header (landing): transparent over the hero at the top,
                // then a light translucent background once scrolled so the nav
                // stays readable. Sticky header (dashboard) keeps its own bg.
                position === 'fixed'
                    ? (scrolled ? 'bg-white/90 backdrop-blur-md' : 'bg-transparent border-transparent')
                    : 'bg-transparent',
                scrolled ? 'shadow-md border-gray-100' : 'border-transparent',
                className,
            )}
        >
            <div className="flex items-center justify-between h-16 sm:h-[72px] px-4 sm:px-6 lg:px-8">
                {/* Left: hamburger + logo */}
                <div className="flex items-center gap-3 flex-shrink-0">
                    {onMenuClick && (
                        <button
                            onClick={onMenuClick}
                            className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            aria-label="Öppna meny"
                        >
                            <Menu className="w-6 h-6 text-gray-600" />
                        </button>
                    )}
                    {!hideLogo && (
                        <Link to="/" className="flex-shrink-0">
                            <img
                                loading="lazy"
                                src={NEXILINK_LOGO_URL}
                                alt="Nexilink Logo"
                                className={cn(
                                    'h-8 sm:h-10 w-auto object-contain transition-all duration-300',
                                    position === 'fixed' && !scrolled && 'brightness-0 invert',
                                )}
                            />
                        </Link>
                    )}
                </div>

                {/* Center: optional content */}
                {children && (
                    <div className="flex-1 min-w-0 mx-4">
                        {children}
                    </div>
                )}

                {/* Right: extra content + user menu */}
                <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                    {rightContent}
                    {user && <UserHeader />}
                </div>
            </div>
        </header>
    );
};

export default AppHeader;
